<script setup lang="ts">
interface Props {
  modelValue: number;
  length: number;
  perPage?: number;
  perPageOptions?: number[];
  showPerPage?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  perPage: 10,
  perPageOptions: () => [10, 25, 50, 100],
  showPerPage: true,
});

const emit = defineEmits<{
  'update:modelValue': [value: number];
  'update:perPage': [value: number];
}>();

const page = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const perPageValue = computed({
  get: () => props.perPage,
  set: (value) => emit('update:perPage', value),
});
</script>

<template>
  <div class="pagination-wrapper">
    <v-pagination
      v-model="page"
      :length="length"
      :total-visible="5"
      show-first-last-page
      class="custom-pagination"
      rounded="circle"
      density="comfortable"
    >
      <template #first>
        <v-btn
          icon
          size="small"
          rounded="circle"
          class="pagination-nav-btn"
          :disabled="page === 1"
          @click="page = 1"
        >
          <v-icon>mdi-chevron-double-left</v-icon>
        </v-btn>
      </template>
      <template #prev>
        <v-btn
          icon
          size="small"
          rounded="circle"
          class="pagination-nav-btn"
          :disabled="page === 1"
          @click="page = page - 1"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
      </template>
      <template #next>
        <v-btn
          icon
          size="small"
          rounded="circle"
          class="pagination-nav-btn"
          :disabled="page === length"
          @click="page = page + 1"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </template>
      <template #last>
        <v-btn
          icon
          size="small"
          rounded="circle"
          class="pagination-nav-btn"
          :disabled="page === length"
          @click="page = length"
        >
          <v-icon>mdi-chevron-double-right</v-icon>
        </v-btn>
      </template>
    </v-pagination>
    <v-select
      v-if="showPerPage"
      v-model="perPageValue"
      :items="perPageOptions"
      density="compact"
      variant="outlined"
      hide-details
      class="per-page-select"
    />
  </div>
</template>

<style scoped lang="scss">
@use '#core/assets/scss/partials/fonts' as *;
@use '#core/assets/scss/partials/colors' as *;

/* Pagination wrapper */
.pagination-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

/* Custom pagination styling */
:deep(.custom-pagination) {
  .v-pagination__list {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .v-pagination__item,
  .v-pagination__first,
  .v-pagination__prev,
  .v-pagination__next,
  .v-pagination__last {
    min-width: 40px;
    height: 40px;
    border-radius: 4px;
    box-shadow: none;

    .v-btn--flat {
      min-width: 40px;
      height: 40px;
      background-color: white !important;
      color: $text-color-primary;
      box-shadow: none;

      &:hover {
        background-color: rgba(0, 0, 0, 0.04);
      }
    }
  }

  .v-pagination__item--is-active {
    .v-btn {
      background-color: white !important;
      color: $text-color-primary !important;

      &::before,
      &::after {
        display: none !important;
      }

      .v-btn__overlay {
        display: none !important;
      }

      &:hover {
        background-color: white !important;
      }
    }
  }

  .v-btn--active {
    background-color: white !important;
  }

  // Navigation buttons (first, prev, next, last)
  .pagination-nav-btn {
    background-color: white !important;
    color: $text-color-primary !important;
    box-shadow: none !important;

    &::before,
    &::after {
      display: none !important;
    }

    .v-btn__overlay {
      display: none !important;
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.04) !important;
    }

    &:disabled {
      opacity: 0.4;
    }
  }
}

/* Per page select */
.per-page-select {
  max-width: 80px;

  :deep(.v-field) {
    background-color: white !important;
  }
}
</style>
